import SearchInput from '@/components/form/SearchInput'
import { FsnImportLogoPreview } from '@/components/imports/fsn/FsnImportLogoPreview'
import { BreakdownRow } from '@/components/imports/fsn/FsnImportMetrics'
import { EmptyState } from '@/components/patterns/empty-state'
import { SelectableSurface } from '@/components/patterns/selectable-surface'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { formatPhone as formatAdminPhone } from '@/hooks/use-phone-formatter'
import { getFsnImportErrorDescription } from '@/lib/services/fsn-imports/fsn-imports.presentation'
import { cn } from '@/lib/utils'
import { AlertCircle, ChevronRight, Loader2, Mail, MapPin, Network, Phone } from 'lucide-react'

function getPhoneCountryCode(country) {
  const normalizedCountry = String(country ?? '')
    .trim()
    .toUpperCase()

  return /^[A-Z]{2}$/.test(normalizedCountry) ? normalizedCountry : 'US'
}

function getFormattedPhone(client) {
  return client.phone ? formatAdminPhone(client.phone, getPhoneCountryCode(client.country)) : null
}

function getCityStatePostalLine(client) {
  const cityState = [client.city, client.state].filter(Boolean).join(', ')

  return [cityState, client.postal_code].filter(Boolean).join(' ')
}

function getAddressLines(client) {
  return [getCityStatePostalLine(client), client.country].filter(Boolean)
}

function ActiveBadge({ active }) {
  return <Badge variant={active ? 'success' : 'muted'}>{active ? 'Active' : 'Inactive'}</Badge>
}

function ContactDetail({ icon: Icon, children, className }) {
  return (
    <div className={cn('flex min-w-0 items-start gap-2 text-sm leading-5', className)}>
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1 text-muted-foreground">{children}</div>
    </div>
  )
}

function AddressLines({ client, emptyLabel = 'No primary address' }) {
  const lines = getAddressLines(client)

  if (lines.length === 0) {
    return <span className="text-muted-foreground">{emptyLabel}</span>
  }

  return (
    <span className="block space-y-0.5">
      {lines.map((line) => (
        <span key={line} className="block break-words">
          {line}
        </span>
      ))}
    </span>
  )
}

function ClientContactSummary({ client }) {
  const phone = getFormattedPhone(client)

  if (!phone && !client.email) {
    return <Typography variant="supporting">No primary contact</Typography>
  }

  return (
    <div className="grid gap-2">
      {phone ? (
        <ContactDetail icon={Phone}>
          <span className="break-words">{phone}</span>
        </ContactDetail>
      ) : null}
      {client.email ? (
        <ContactDetail icon={Mail}>
          <span className="break-words [overflow-wrap:anywhere]">{client.email}</span>
        </ContactDetail>
      ) : null}
    </div>
  )
}

function ClientResultButton({ client, selected, onSelect, disabled = false }) {
  return (
    <SelectableSurface
      type="button"
      selected={selected}
      radius="surface"
      className={cn(
        '@container/fsn-client-result w-full px-4 py-4 text-left',
        selected
          ? 'border-primary/50 bg-primary/10 text-foreground'
          : 'border-border/70 bg-background hover:border-primary/35 hover:bg-muted/45'
      )}
      onClick={() => onSelect(client)}
      disabled={disabled}
    >
      <div className="grid gap-4 @[42rem]/fsn-client-result:grid-cols-[minmax(0,0.9fr)_minmax(15rem,1fr)_auto] @[42rem]/fsn-client-result:items-start">
        <div className="min-w-0 space-y-2">
          <div className="flex min-w-0 flex-wrap items-start gap-2">
            <Typography variant="label" className="min-w-0 break-words">
              {client.name}
            </Typography>
            <ActiveBadge active={client.active} />
          </div>
          <Typography variant="label" tone="muted" className="block">
            Client ID {client.id}
          </Typography>
        </div>

        <div className="grid min-w-0 gap-3 @[32rem]/fsn-client-result:grid-cols-2 @[42rem]/fsn-client-result:grid-cols-1">
          <ClientContactSummary client={client} />
          <ContactDetail icon={MapPin}>
            <AddressLines client={client} />
          </ContactDetail>
        </div>

        <div className="flex items-center justify-end">
          <ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />
        </div>
      </div>
    </SelectableSurface>
  )
}

function SelectedClientPanel({ client, onChangeClient, changeDisabled = false }) {
  return (
    <div className="@container/selected-client rounded-md border border-border/70 bg-muted/20 p-4">
      <div className="grid gap-4 @3xl/selected-client:grid-cols-[minmax(0,1fr)_minmax(12rem,0.8fr)_minmax(12rem,0.8fr)_auto] @3xl/selected-client:items-start">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-start gap-2">
            <Typography variant="label" className="min-w-0 break-words">
              {client.name}
            </Typography>
            <ActiveBadge active={client.active} />
          </div>
          <Typography variant="label" tone="muted" className="mt-2 block">
            Client ID {client.id}
          </Typography>
        </div>
        <ClientContactSummary client={client} />
        <ContactDetail icon={MapPin}>
          <AddressLines client={client} emptyLabel="No address" />
        </ContactDetail>
        <div className="@3xl/selected-client:justify-self-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onChangeClient}
            disabled={changeDisabled}
          >
            Change client
          </Button>
        </div>
      </div>
    </div>
  )
}

function EstimatePanel({ estimate, isLoading, error }) {
  if (!estimate && !isLoading && !error) {
    return null
  }

  if (isLoading) {
    return (
      <div className="rounded-md border border-border/70 p-4">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Loading FSN details...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="warning">
        <AlertCircle className="size-4" />
        <AlertTitle>Estimate unavailable</AlertTitle>
        <AlertDescription>
          {getFsnImportErrorDescription(
            error,
            'Flower Shop Network counts are unavailable. You can still try previewing the import.'
          )}
        </AlertDescription>
      </Alert>
    )
  }

  if (estimate.status === 'account_required') {
    return (
      <Alert variant="warning">
        <AlertCircle className="size-4" />
        <AlertTitle>Choose a website account</AlertTitle>
        <AlertDescription>
          This FSN client has multiple website accounts. Choose the current storefront account to
          load counts and logo details.
        </AlertDescription>
      </Alert>
    )
  }

  const availableSections = estimate.available_sections
  const productsAvailable = !availableSections || availableSections.includes('products')
  const customersAvailable = !availableSections || availableSections.includes('customers')
  const logoAvailable = !availableSections || availableSections.includes('logo')

  return (
    <div className="@container/fsn-estimate space-y-4 rounded-md border border-border/70 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Typography variant="label">FSN details loaded</Typography>
          <Typography variant="supporting" className="mt-1 block">
            Use these counts and the logo preview to confirm this is the right florist.
          </Typography>
        </div>
        <Badge variant="secondary">Loaded</Badge>
      </div>

      <div className="grid gap-4 @3xl/fsn-estimate:grid-cols-[minmax(0,1fr)_minmax(10rem,0.6fr)]">
        <div className="grid min-w-0 gap-2 @2xl/fsn-estimate:grid-cols-2">
          <BreakdownRow
            label="Products on active pages"
            value={productsAvailable ? estimate.products.current_page_total : 'Not available'}
          />
          <BreakdownRow
            label="All active FSN products"
            value={productsAvailable ? estimate.products.fsn.all_active : 'Not available'}
          />
          <BreakdownRow
            label="Custom products"
            value={productsAvailable ? estimate.products.custom.all : 'Not available'}
          />
          <BreakdownRow
            label="Notification contacts"
            value={estimate.notification_contacts?.total ?? 0}
          />
          <BreakdownRow
            label="Location details"
            value={estimate.location?.available ? 'Yes' : 'No'}
          />
          <BreakdownRow
            label="Delivery fees"
            value={
              (estimate.delivery_areas?.cities ?? 0) +
              (estimate.delivery_areas?.postal_codes ?? 0) +
              (estimate.delivery_areas?.distance_ranges ?? 0)
            }
          />
          <BreakdownRow
            label="Customers"
            value={customersAvailable ? (estimate.customers?.total ?? 0) : 'Not available'}
          />
          <BreakdownRow label="Reviews" value={estimate.reviews?.total ?? 0} />
          <BreakdownRow label="Add-ons" value={estimate.add_ons?.total ?? 0} />
          <BreakdownRow label="Social links" value={estimate.social_links?.total ?? 0} />
          <BreakdownRow
            label="Logo found"
            value={logoAvailable ? (estimate.logo?.available ? 'Yes' : 'No') : 'Not available'}
          />
        </div>

        <div className="min-w-0 space-y-2">
          <Typography variant="label" tone="muted">
            Logo preview
          </Typography>
          <FsnImportLogoPreview
            key={estimate.logo?.url ?? 'empty-logo'}
            logoUrl={estimate.logo?.url}
          />
        </div>
      </div>
    </div>
  )
}

function NetworkOnlyClientNotice({ estimate }) {
  if (estimate?.status !== 'ready' || estimate.account || estimate.accounts?.length) {
    return null
  }

  return (
    <Alert>
      <Network className="size-4" />
      <AlertTitle>FSN network data available</AlertTitle>
      <AlertDescription>
        This client does not have an FSN website account. Location, delivery, reviews, add-ons,
        contacts, and social links can still be imported. Website products, website customer
        history, and logo are unavailable.
      </AlertDescription>
    </Alert>
  )
}

function FsnImportClientStep({
  showClientSearch,
  searchTerm,
  setSearchTerm,
  canSearch,
  isSearching,
  unavailableMessage,
  showEmptyState,
  clients,
  resultCountLabel,
  selectedClient,
  accountSelector,
  onSelectClient,
  onChangeClient,
  estimate,
  estimateLoading,
  estimateError,
  isImportRunning,
}) {
  return (
    <div className="space-y-5">
      {showClientSearch ? (
        <div className="min-w-0 space-y-4">
          <SearchInput
            value={searchTerm}
            setValue={setSearchTerm}
            onClear={() => setSearchTerm('')}
            name="fsn-client-search"
            aria-label="Search FSN clients"
            placeholder="Search FSN clients"
            disabled={isImportRunning}
          />

          {!canSearch ? (
            <Typography variant="supporting" className="block">
              Enter at least 3 characters, or 2 digits for a client ID.
            </Typography>
          ) : null}

          {isSearching ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              Searching FSN clients...
            </div>
          ) : null}

          {unavailableMessage ? (
            <Alert variant="warning">
              <AlertCircle className="size-4" />
              <AlertTitle>FSN lookup unavailable</AlertTitle>
              <AlertDescription>{unavailableMessage}</AlertDescription>
            </Alert>
          ) : null}

          {showEmptyState ? (
            <Typography variant="supporting" className="block">
              No FSN clients matched that search.
            </Typography>
          ) : null}

          {clients.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Typography variant="label" tone="muted">
                  Search results
                </Typography>
                <Typography variant="label" tone="muted">
                  {resultCountLabel}
                </Typography>
              </div>
              <div className="space-y-2">
                {clients.map((client) => (
                  <ClientResultButton
                    key={client.id}
                    client={client}
                    selected={selectedClient?.id === client.id}
                    onSelect={onSelectClient}
                    disabled={isImportRunning}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {selectedClient ? (
        <div className="space-y-4">
          <SelectedClientPanel
            client={selectedClient}
            onChangeClient={onChangeClient}
            changeDisabled={isImportRunning}
          />
          {accountSelector}
          <NetworkOnlyClientNotice estimate={estimate} />
          <EstimatePanel estimate={estimate} isLoading={estimateLoading} error={estimateError} />
        </div>
      ) : (
        <EmptyState size="compact" surface="transparent" title="No client selected." />
      )}
    </div>
  )
}

export { FsnImportClientStep }
